import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { VideoPlayer } from '../../components'
import { useLocation, useNavigate } from 'react-router-dom'
import { ModuleBackground, ReturnButton } from '../../components/molecules'
import StudentMenu from '../../components/organisms/Menu/StudentMenu'
import { getVideoUrl } from '../../utils/video'
import { Button, Text } from '../../components/atoms'
import apiServices from '../../libs/services'
import { useDispatch, useSelector } from 'react-redux'
import { modules } from '../../utils'
import { RootState } from '../../redux/store'
import { Module } from '../../models/global.types'
import { setModules } from '../../redux/slices/moduleSlice'
import { ControlHighlight, KeyboardControl } from '../../components/molecules/KeyboardControls'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'

const placeholderButtonVideoPart = <span className={`btn-replay-video-part`}/>

export const idButtonVideoPrev = 'button-video-replay-previous'
export const idButtonVideoNext = 'button-video-replay-next'

const ModuleReplay: React.FC = () => {
  const keyboardControls = useKeyboardControls()

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const modulesSlice: Array<Module> = useSelector((state: RootState) => state.module.modules)

  const [module, setModule] = useState<Module | null>(null)
  const [modulePart, setModulePart] = useState<number>(1)
  const [videoUrl, setVideoUrl] = useState('')
  const [captionsUrl, setCaptionsUrl] = useState('')

  const query = new URLSearchParams(location.search)
  const moduleName = modules.parseModuleName(query.get('module'))
  const a11yParam = query.get('a11y') === 'true'

  const fetchModules = useCallback(async () => {
    if (!Array.isArray(modulesSlice) || modulesSlice.length <= 0) {
      const res = await apiServices.fetchModules()
      const modulesArr = res?.data?.data?.modules
      const m = modulesArr.filter(
        (module: Module) => `${module.name}`.toLowerCase() !== 'lightning').sort((a: any, b: any) => a.sort - b.sort)
      dispatch(setModules(m))
    }
  }, [dispatch, modulesSlice])

  /// Initial data fetching
  useEffect(() => {
    // Get modules if the store doesn't have them.
    // TODO: refactor with Redux flow.
    if (!Array.isArray(modulesSlice) || modulesSlice.length <= 0) {
      fetchModules()
    }

    const module = modulesSlice.find(m => m.name.toLowerCase() === moduleName)
    if (module === undefined) { return }

    setModule(module)
    setModulePart(1)
  }, [fetchModules, moduleName, modulesSlice])

  /// Update current video
  useEffect(() => {
    if (module === undefined || module === null) { return }

    const [videoUrl, captionsUrl] = getVideoUrl(module.name, modulePart, a11yParam)
    setVideoUrl(videoUrl)
    setCaptionsUrl(captionsUrl)
  }, [module, a11yParam, modulePart])

  const onVideoEnded = (): void => {
    navigate('/user-dashboard')
  }

  const renderButtonReturn = useMemo((): ReactNode => {
    return (
      <ReturnButton
        id='return-button-module-replay'
        onClick={() => navigate('/user-dashboard')}
        text='Return to Dashboard'
      />
    )
  }, [navigate])

  const renderButtonVideo = useCallback((id: string, text: string, onClick: (() => void)): ReactNode => {
    return (
    <ControlHighlight
      active={keyboardControls.isEnabled && keyboardControls.isElementFocused(id)}
      highlightEffect={HighlightEffect.ButtonHexOutline}
      data-testid={`highlight-${id}`}
    >
      <KeyboardControl>
        <Button
          id={id}
          data-testid={id}
          bgColor='bg-dopage-lightGreen'
          className={`diamond-btn-sm btn-replay-video-part font-bold p-[3px]`}
          tabIndex={-1}
          onClick={onClick}
        >
          <div className='bg-dopage-black flex justify-center items-center diamond-btn-sm'>
            <Text
              className='font-bold text-center text-[26px] text-dopage-lightGreen'
              bold
              size='SM'
              text={text}
            />
          </div>
        </Button>
      </KeyboardControl>
    </ControlHighlight>
    )
  }, [keyboardControls])

  const renderButtonVideoPrev = useMemo((): ReactNode => {
    if (module === null) { return placeholderButtonVideoPart }
    if (modulePart === 1) { return placeholderButtonVideoPart }

    return renderButtonVideo(idButtonVideoPrev, '< Previous', () => {
      let newPart = modulePart - 1
      if (newPart < 1) {
        newPart = 1
      }
      setModulePart(newPart)
    })
  }, [module, modulePart, renderButtonVideo])

  const renderButtonVideoNext = useMemo((): ReactNode => {
    if (module === null) { return placeholderButtonVideoPart }
    if (modulePart === module.partCount) { return placeholderButtonVideoPart }

    return renderButtonVideo(idButtonVideoNext, 'Next Video >', () => {
      let newPart = modulePart + 1
      if (newPart > module.partCount) {
        newPart = module.partCount
      }
      setModulePart(newPart)
    })

  }, [module, modulePart, renderButtonVideo])

  if (module !== null) {
    return (
      <ModuleBackground
        moduleName={moduleName}
        className='p-4 flex flex-col'
        data-testid='background-module-replay'
      >
        <StudentMenu />
        <div className='flex justify-center items-tops p-8'>
          <div className='h-[70vh] min-h-[200px]'>
            <VideoPlayer
              handleEnded={onVideoEnded}
              instructionMode={false}
              pauseOnFocusLost={false}
              isPlaying
              url={videoUrl}
              subTitlesUrl={captionsUrl}
              secondaryLeftComponent={renderButtonReturn}
              controlLeftComponent={renderButtonVideoPrev}
              controlRightComponent={renderButtonVideoNext}
            />
          </div>
        </div>
      </ModuleBackground>
    )
  } else {
    return null
  }
}

export default ModuleReplay
